import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Send } from "lucide-react";

import {
  BASIC_DIALOG_ACTION_CLASS,
  BasicDialogFrame,
} from "@/components/ui/BasicDialog.jsx";
import Button from "@/components/ui/Button.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import Select from "@/components/ui/Select.jsx";
import DesktopHeaderTabs from "@/components/nav/DesktopHeaderTabs.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { logout } from "@/lib/auth/logout.js";
import {
  FEEDBACK_AREAS,
  FEEDBACK_TYPES,
  ROLE_CONTEXTS,
  SEVERITIES,
} from "@/pages/feedback/feedbackOptions.js";
import {
  resolveFeedbackProfileRole,
  sendFeedbackMagicLink,
  submitFeedbackReport,
} from "@/pages/feedback/feedbackReportsService.js";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import TopBar from "@/sot/primitives/TopBar.jsx";

const DEFAULT_FORM = {
  feedbackType: "General suggestion",
  roleContext: "",
  area: "General platform",
  severity: "Medium",
  message: "",
  contactOk: false,
};

function currentRouteFromLocation(location) {
  const params = new URLSearchParams(location.search || "");
  const referrer = typeof document !== "undefined" ? document.referrer : "";
  const baseRoute = params.get("route") || params.get("from") || referrer || "";
  const details = [
    params.get("section") ? `section=${params.get("section")}` : "",
    params.get("role") ? `role=${params.get("role")}` : "",
    params.get("entityType") || params.get("entityId")
      ? `entity=${[params.get("entityType"), params.get("entityId")].filter(Boolean).join(":")}`
      : "",
    params.get("returnTo") ? `return=${params.get("returnTo")}` : "",
  ].filter(Boolean);

  return [baseRoute, ...details].filter(Boolean).join(" | ");
}

function returnRouteFromLocation(location) {
  const params = new URLSearchParams(location.search || "");
  return params.get("returnTo") || params.get("from") || "/home";
}

function roleContextFromQuery(value) {
  const role = String(value || "").trim().toLowerCase();
  if (role === "doctor") return "Clinician";
  if (role === "patient") return "Patient";
  if (role === "pharmacy") return "Pharmacy / lab";
  if (role.includes("admin") || role === "moh" || role === "institution") return "Institution / MoH";
  return "";
}

function areaFromSection(value) {
  const section = String(value || "").trim();
  if (!section) return "";
  if (section.startsWith("appointments-book") || section.startsWith("appointments-edit")) return "Booking";
  if (section.startsWith("appointments")) return "Booking";
  if (section.startsWith("prescriptions")) return "myEHR / records";
  if (section.startsWith("pharmacy")) return "General platform";
  if (section.startsWith("settings")) return "General platform";
  return "";
}

function FieldLabel({ htmlFor, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-title-small text-on-surface"
    >
      {children}
    </label>
  );
}

function toSelectOptions(options) {
  return options.map((option) =>
    typeof option === "string" ? { value: option, label: option } : option
  );
}

function SelectField({ id, value, onChange, options }) {
  return (
    <Select
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      options={toSelectOptions(options)}
      placeholder="Select"
      indicatorSrc="/icons/clinicians/expand-more.svg"
      triggerClassName="mt-2 bg-[rgb(var(--clr-surface-cont-highest))] px-4 pr-5 text-body-large shadow-[var(--elev-1)]"
      valueClassName="min-w-0"
      iconClassName="h-4 w-4 opacity-80"
      contentClassName="bg-[rgb(var(--clr-surface-cont-highest))]"
      optionClassName="text-body-medium"
    />
  );
}

function FeedbackShell({ children }) {
  const navigate = useNavigate();
  return (
    <main className="page min-h-[100svh] px-4 pb-10 pt-2 md:px-0">
      <TopBar
        title="Staging Feedback"
        subtitle="Share what worked, what felt unclear, or what needs attention in preparation for production."
        onBack={() => navigate("/home")}
        titleWrapperClassName="mt-1 mb-5 border-b border-[rgba(var(--clr-outline-variant),0.7)] pb-5"
      />
      {children}
    </main>
  );
}

function SignedInFeedbackShell({ children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-[100svh] bg-[rgb(var(--clr-surface-container-low))]">
      <DesktopHeaderTabs />
      <main className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <div className="mb-5 border-b border-[rgba(var(--clr-outline-variant),0.7)]">
          <TopBar
            title="Staging Feedback"
            subtitle="Share what worked, what felt unclear, or what needs attention in preparation for production."
            titleClassName="text-headline-small text-on-surface"
            onBack={() => navigate("/home")}
          />
        </div>
        {children}
      </main>
    </div>
  );
}

function AuthPrompt() {
  const [email, setEmail] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState("");

  async function handleSubmit(event) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await sendFeedbackMagicLink(email);
      setSent(true);
    } catch (nextError) {
      setError(nextError?.message || "We could not send the sign-in link yet.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <FeedbackShell>
      <div className="afrimed-card p-4 md:p-5">
        <h2 className="text-title-medium text-on-surface">
          Secure feedback sign-in
        </h2>
        <p className="mt-1 text-body-medium text-on-surface-var">
          Sign in with a magic link to leave staging feedback.
        </p>

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          <TextField56
            id="feedback-email"
            label="Email address"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            placeholder="you@example.com"
            tone="surface"
          />

          {error ? (
            <div className="rounded-[12px] bg-[rgba(var(--clr-error),0.10)] px-4 py-3 text-body-medium text-[rgb(var(--clr-error))]">
              {error}
            </div>
          ) : null}

          {sent ? (
            <div className="rounded-[12px] bg-[rgba(var(--afrimed-primary),0.08)] px-4 py-3 text-body-medium text-[rgb(var(--afrimed-primary))]">
              Check your email for the secure sign-in link, then return here.
            </div>
          ) : null}

          <Button
            type="submit"
            disabled={busy || !String(email || "").trim()}
            size="lg"
            fullWidth
            className="gap-2"
          >
            <Send className="h-4 w-4" aria-hidden />
            {busy ? "Sending..." : "Send magic link"}
          </Button>
        </form>
      </div>
    </FeedbackShell>
  );
}

export function FeedbackForm({ shell = "default", onSubmitted = null } = {}) {
  const auth = useAuth() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = React.useState(DEFAULT_FORM);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const routeContext = currentRouteFromLocation(location);
  const params = React.useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search]
  );
  const signoutIntent = params.get("intent") === "signout";
  const returnTo = returnRouteFromLocation(location);

  React.useEffect(() => {
    const profileRole = resolveFeedbackProfileRole(auth);
    const queryRole = roleContextFromQuery(params.get("role"));
    const queryArea = areaFromSection(params.get("section"));
    setForm((current) => {
      if (current.roleContext && current.area !== DEFAULT_FORM.area) return current;
      return {
        ...current,
        roleContext: current.roleContext || queryRole ||
          (profileRole === "doctor"
            ? "Clinician"
            : profileRole === "patient"
              ? "Patient"
              : profileRole === "pharmacy"
                ? "Pharmacy / lab"
                : profileRole.includes("admin")
                  ? "Institution / MoH"
                  : ""),
        area: current.area === DEFAULT_FORM.area && queryArea ? queryArea : current.area,
      };
    });
  }, [auth, params]);

  function update(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const row = await submitFeedbackReport({
        auth,
        feedbackType: form.feedbackType,
        roleContext: form.roleContext,
        area: form.area,
        severity: form.severity,
        message: form.message,
        contactOk: form.contactOk,
        routeContext,
        referrer: typeof document !== "undefined" ? document.referrer : "",
      });
      if (typeof onSubmitted === "function") {
        onSubmitted(row);
      } else {
        const next = new URLSearchParams();
        if (row?.id) next.set("id", row.id);
        if (signoutIntent) next.set("intent", "signout");
        navigate(`/feedback/thanks${next.toString() ? `?${next.toString()}` : ""}`, {
          replace: true,
        });
      }
    } catch (nextError) {
      setError(nextError?.message || "We could not save your feedback yet.");
    } finally {
      setBusy(false);
    }
  }

  const formContent = (
        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <FieldLabel htmlFor="feedback-type">Feedback type</FieldLabel>
              <SelectField
                id="feedback-type"
                value={form.feedbackType}
                onChange={(value) => update("feedbackType", value)}
                options={FEEDBACK_TYPES}
              />
            </div>
            <div>
              <FieldLabel htmlFor="feedback-role">Your context</FieldLabel>
              <SelectField
                id="feedback-role"
                value={form.roleContext}
                onChange={(value) => update("roleContext", value)}
                options={ROLE_CONTEXTS}
              />
            </div>
            <div>
              <FieldLabel htmlFor="feedback-area">Area</FieldLabel>
              <SelectField
                id="feedback-area"
                value={form.area}
                onChange={(value) => update("area", value)}
                options={FEEDBACK_AREAS}
              />
            </div>
            <div>
              <FieldLabel htmlFor="feedback-severity">Severity</FieldLabel>
              <SelectField
                id="feedback-severity"
                value={form.severity}
                onChange={(value) => update("severity", value)}
                options={SEVERITIES}
              />
            </div>
          </div>

          <div>
            <FieldLabel htmlFor="feedback-message">What should we know?</FieldLabel>
            <div className="afrimed-card mt-2 p-4">
              <textarea
                id="feedback-message"
                value={form.message}
                onChange={(event) => update("message", event.target.value)}
                rows={6}
                maxLength={4000}
                placeholder="A few specific notes are more useful than a polished essay."
                className="block min-h-[144px] w-full resize-y border-none bg-transparent p-0 text-body-large text-on-surface outline-none placeholder:text-on-surface-var focus:ring-0"
              />
            </div>
          </div>

          {routeContext ? (
            <div className="rounded-[12px] bg-[rgba(var(--clr-on-surface),0.04)] px-4 py-3 text-body-small text-on-surface-var">
              Context captured: {routeContext}
            </div>
          ) : null}

          <div className="flex items-start gap-3 text-body-medium text-on-surface">
            <Checkbox
              checked={form.contactOk}
              onChange={(checked) => update("contactOk", checked)}
              className="mt-0.5 shrink-0"
            />
            <button
              type="button"
              className="min-w-0 flex-1 text-left"
              onClick={() => update("contactOk", !form.contactOk)}
            >
              AfriMed™ may contact me about this feedback.
            </button>
          </div>

          {error ? (
            <div className="rounded-[12px] bg-[rgba(var(--clr-error),0.10)] px-4 py-3 text-body-medium text-[rgb(var(--clr-error))]">
              {error}
            </div>
          ) : null}

          <div className="flex flex-col gap-3 border-t border-[rgba(var(--clr-outline-variant),0.7)] pt-5 sm:flex-row sm:items-center sm:justify-start">
            <Button
              type="button"
              variant="text"
              size="lg"
              onClick={() => navigate(returnTo)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || !String(form.message || "").trim()}
              size="lg"
              className="gap-2"
            >
              <Send className="h-4 w-4" aria-hidden />
              {busy ? "Sending..." : "Send feedback"}
            </Button>
          </div>
        </form>
  );

  return shell === "bare" ? formContent : <SignedInFeedbackShell>{formContent}</SignedInFeedbackShell>;
}

export default function FeedbackPortal() {
  const auth = useAuth() || {};
  if (auth?.loading && !auth?.user?.id) {
    return (
      <div className="page grid min-h-[100svh] place-items-center text-body-medium text-on-surface-var">
        Loading feedback...
      </div>
    );
  }
  return auth?.user?.id ? <FeedbackForm /> : <AuthPrompt />;
}

export function FeedbackThanks() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loggingOut, setLoggingOut] = React.useState(false);
  const params = new URLSearchParams(location.search || "");
  const signoutIntent = params.get("intent") === "signout";

  async function continueSignOut() {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout({ redirectTo: "/login?logout=1" });
    } catch {
      window.location.href = "/login?logout=1";
    }
  }

  return (
    <main className="page flex min-h-[100svh] items-center justify-center bg-[rgb(var(--clr-surface-container-low))] px-4 py-8">
      <BasicDialogFrame
        as="section"
        aria-labelledby="feedback-thanks-title"
        titleId="feedback-thanks-title"
        iconSrc="/icons/dialog-check.svg"
        title="Thank you"
        body="Your feedback has been saved for the AfriMed™ team."
        className="w-[312px] max-w-[calc(100vw-48px)] min-w-0"
        contentClassName="pt-6"
        actionsClassName={`${signoutIntent ? "flex-col " : ""}px-6`}
      >
        <button
          type="button"
          onClick={() => navigate("/feedback")}
          className={`${BASIC_DIALOG_ACTION_CLASS} border-transparent bg-[var(--state-on-surface-08)] text-primary`}
        >
          Return to feedback
        </button>
        {signoutIntent ? (
          <button
            type="button"
            onClick={continueSignOut}
            disabled={loggingOut}
            className={`${BASIC_DIALOG_ACTION_CLASS} border-transparent bg-transparent text-primary disabled:opacity-60`}
          >
            {loggingOut ? "Signing out..." : "Continue sign out"}
          </button>
        ) : null}
      </BasicDialogFrame>
    </main>
  );
}
